@let utilizationsKpisFeatureFlagEnabled =
  !!('portal-shift-timeline-utilization-kpis' | featureFlagPure | async);
@let hasShiftReadPermission = can(RoleAction.Read, RolePermissionSubject.Shift);
@let hasUserReadPermission = can(RoleAction.Read, RolePermissionSubject.User);
@let hasPayrollFeature = !!(FeatureName.Payroll | featurePure | async);
@let forbiddenMessage = 'general.forbidden_action' | translate;
@let noFeatureMessage =
  'shift_timeline.kpi_setting.no_feature_access' | translate;

<div class="mb-3">
  <div class="section-title">
    {{ 'shift_timeline.kpi_setting.sort.title' | translate }}
  </div>
  <div class="my-1 d-flex">
    <nz-select
      class="me-2"
      [(ngModel)]="selectedSortByOption"
      data-test-id="wilson-shift-timeline-kpi-setting-popup:sort-by-option-select"
    >
      <nz-option
        [nzLabel]="'general.name' | translate"
        [nzValue]="SortByOptions.Name"
      ></nz-option>
      <nz-option
        [nzLabel]="'general.profession' | translate"
        [nzValue]="SortByOptions.Profession"
      ></nz-option>
    </nz-select>
    <nz-segmented
      [nzOptions]="sortOptions"
      [(ngModel)]="selectedSortDirection"
    ></nz-segmented>
  </div>
</div>

<div class="mb-3">
  <div class="section-title">
    {{ 'shift_timeline.kpi_setting.color_options' | translate }}
  </div>
  <div class="my-1">
    <nz-select [(ngModel)]="selectedShiftColorOption">
      <nz-option
        [nzLabel]="
          'shift_timeline.kpi_setting.color_options.shift_category' | translate
        "
        [nzValue]="ShiftColorOptions.ShiftCategory"
      ></nz-option>
      @if (can(RoleAction.Read, RolePermissionSubject.Label)) {
        <nz-option
          [nzLabel]="
            'shift_timeline.kpi_setting.color_options.label' | translate
          "
          [nzValue]="ShiftColorOptions.Label"
        ></nz-option>
      }
      @if (can(RoleAction.Read, RolePermissionSubject.Project)) {
        <nz-option
          [nzLabel]="
            'shift_timeline.kpi_setting.color_options.project' | translate
          "
          [nzValue]="ShiftColorOptions.Project"
        ></nz-option>
      }
    </nz-select>
  </div>
</div>

<div class="mb-3">
  <div class="section-title">
    {{ 'shift_timeline.kpi_setting.main_kpi' | translate }}
  </div>
  <div class="my-1">
    @let isDisabled =
      !hasShiftReadPermission || !hasUserReadPermission || !hasPayrollFeature;
    @let disabledTooltipTitle =
      !hasShiftReadPermission || !hasUserReadPermission
        ? forbiddenMessage
        : !hasPayrollFeature
          ? noFeatureMessage
          : '';
    <nz-select
      [nzPlaceHolder]="'KPIs'"
      [nzMode]="'multiple'"
      [nzMaxMultipleCount]="3"
      [(ngModel)]="selectedKPIs"
    >
      <nz-option
        nzCustomContent
        [nzLabel]="'shift_timeline.kpi_setting.overtime' | translate"
        [nzValue]="KPIOptions.OvertimeAccount"
        [nzDisabled]="isDisabled"
      >
        <div nz-tooltip [nzTooltipTitle]="disabledTooltipTitle">
          {{ 'shift_timeline.kpi_setting.overtime' | translate }}
        </div>
      </nz-option>
      <nz-option
        [nzLabel]="'shift_timeline.kpi_setting.productivity' | translate"
        [nzValue]="KPIOptions.Productivity"
      ></nz-option>
      @if (utilizationsKpisFeatureFlagEnabled) {
        @let workTimeWeek =
          ('shift_timeline.kpi_setting.work_time' | translate) +
          ' (' +
          ('general.week' | translate) +
          ')';
        @let plannedTimeWeek =
          ('shift_timeline.kpi_setting.planned_time' | translate) +
          ' (' +
          ('general.week' | translate) +
          ')';
        @let overtimeWeek =
          ('shift_timeline.kpi_setting.overtimes' | translate) +
          ' (' +
          ('general.week' | translate) +
          ')';
        @let workTimeMonth =
          ('shift_timeline.kpi_setting.work_time' | translate) +
          ' (' +
          ('general.month' | translate) +
          ')';
        @let plannedTimeMonth =
          ('shift_timeline.kpi_setting.planned_time' | translate) +
          ' (' +
          ('general.month' | translate) +
          ')';
        @let overtimeMonth =
          ('shift_timeline.kpi_setting.overtimes' | translate) +
          ' (' +
          ('general.month' | translate) +
          ')';
        <nz-option
          nzCustomContent
          [nzLabel]="workTimeWeek"
          [nzValue]="KPIOptions.WorkTimeWeek"
          [nzDisabled]="isDisabled"
        >
          <div nz-tooltip [nzTooltipTitle]="disabledTooltipTitle">
            {{ workTimeWeek }}
          </div>
        </nz-option>
        <nz-option
          nzCustomContent
          [nzLabel]="plannedTimeWeek"
          [nzValue]="KPIOptions.PlannedTimeWeek"
          [nzDisabled]="isDisabled"
        >
          <div nz-tooltip [nzTooltipTitle]="disabledTooltipTitle">
            {{ plannedTimeWeek }}
          </div>
        </nz-option>
        <nz-option
          nzCustomContent
          [nzLabel]="overtimeWeek"
          [nzValue]="KPIOptions.OvertimeWeek"
          [nzDisabled]="isDisabled"
        >
          <div nz-tooltip [nzTooltipTitle]="disabledTooltipTitle">
            {{ overtimeWeek }}
          </div>
        </nz-option>
        <nz-option
          nzCustomContent
          [nzLabel]="workTimeMonth"
          [nzValue]="KPIOptions.WorkTimeMonth"
          [nzDisabled]="isDisabled"
        >
          <div nz-tooltip [nzTooltipTitle]="disabledTooltipTitle">
            {{ workTimeMonth }}
          </div>
        </nz-option>
        <nz-option
          nzCustomContent
          [nzLabel]="plannedTimeMonth"
          [nzValue]="KPIOptions.PlannedTimeMonth"
          [nzDisabled]="isDisabled"
        >
          <div nz-tooltip [nzTooltipTitle]="disabledTooltipTitle">
            {{ plannedTimeMonth }}
          </div>
        </nz-option>
        <nz-option
          nzCustomContent
          [nzLabel]="overtimeMonth"
          [nzValue]="KPIOptions.OvertimeMonth"
          [nzDisabled]="isDisabled"
        >
          <div nz-tooltip [nzTooltipTitle]="disabledTooltipTitle">
            {{ overtimeMonth }}
          </div>
        </nz-option>
      }
      <nz-option
        [nzLabel]="'shift_timeline.kpi_setting.home_location' | translate"
        [nzValue]="KPIOptions.HomeLocation"
      ></nz-option>
    </nz-select>
  </div>
  <div class="description-text">
    {{ 'shift_timeline.kpi_setting.main_kpi_description' | translate }}
  </div>
</div>

<div class="mb-3">
  <div class="section-title">
    {{ 'shift_timeline.kpi_setting.min_threshold' | translate }}
  </div>
  <div class="my-1">
    <input
      type="number"
      class="form-control rounded-0"
      [(ngModel)]="selectedThreshold.min"
      [placeholder]="'general.time.hours_plural' | translate"
    />
  </div>
</div>

<div class="mb-3">
  <div class="section-title">
    {{ 'shift_timeline.kpi_setting.max_threshold' | translate }}
  </div>
  <div class="my-1">
    <input
      type="number"
      class="form-control rounded-0"
      [(ngModel)]="selectedThreshold.max"
      [placeholder]="'general.time.hours_plural' | translate"
    />
  </div>
</div>

<div>
  <div class="section-title">
    {{ 'shift_timeline.kpi_setting.layout.title' | translate }}
  </div>
  <div class="mt-1">
    <nz-radio-group [(ngModel)]="selectedStaffLayoutStyle">
      <label
        data-test-id="wilson-shift-timeline-kpi-setting-popup:staff-layout-style-radio:compact"
        nz-radio
        [nzValue]="StaffLayoutStyle.Compact"
        class="mt-1"
      >
        <span class="layout-text">
          {{ 'shift_timeline.kpi_setting.layout.compact' | translate }}
        </span>
        <span class="description-text d-block">
          {{
            'shift_timeline.kpi_setting.layout.compact.description' | translate
          }}
        </span>
      </label>
      @if ('portal-timeline-minimized-view' | featureFlagPure | async) {
        <label
          nz-radio
          [nzValue]="StaffLayoutStyle.Minimal"
          class="mt-1"
          data-test-id="wilson-shift-timeline-kpi-setting-popup:staff-layout-style-radio:minimal"
        >
          <span class="layout-text">
            {{ 'shift_timeline.kpi_setting.layout.minimal' | translate }}
          </span>
          <span class="description-text d-block">
            {{
              'shift_timeline.kpi_setting.layout.minimal.description'
                | translate
            }}
          </span>
        </label>
      }
    </nz-radio-group>
  </div>
</div>

<div>
  <div class="section-title">
    {{ 'shift_timeline.kpi_setting.assignment_layout.title' | translate }}
  </div>
  <div class="mt-1">
    <nz-radio-group [(ngModel)]="selectedAssignmentLayoutStyle">
      @if ('portal-timeline-cell-view-mode' | featureFlagPure | async) {
        <label
          data-test-id="wilson-shift-timeline-kpi-setting-popup:assignment-layout-style-radio:cell-view"
          nz-radio
          [nzValue]="AssignmentLayoutStyle.CellView"
          class="mt-1"
        >
          <span class="layout-text">
            {{
              'shift_timeline.kpi_setting.assignment_layout.cell_view'
                | translate
            }}
          </span>
          <span class="description-text d-block">
            {{
              'shift_timeline.kpi_setting.assignment_layout.cell_view.description'
                | translate
            }}
          </span>
        </label>
      }
      <label
        data-test-id="wilson-shift-timeline-kpi-setting-popup:assignment-layout-style-radio:timeline-view"
        nz-radio
        [nzValue]="AssignmentLayoutStyle.TimelineView"
        class="mt-1"
      >
        <span class="layout-text">
          {{
            'shift_timeline.kpi_setting.assignment_layout.timeline_view'
              | translate
          }}
        </span>
        <span class="description-text d-block">
          {{
            'shift_timeline.kpi_setting.assignment_layout.timeline_view.description'
              | translate
          }}
        </span>
      </label>
    </nz-radio-group>
  </div>
</div>
